import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null, organization: null, role: null, modules: [], subscription: null, organizations: [] });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setState({ loading: false, user: data.user, organization: data.organization, role: data.role,
                 modules: data.modules || [], subscription: data.subscription || null,
                 organizations: data.organizations || [] });
    } catch {
      setState({ loading: false, user: null, organization: null, role: null, modules: [], subscription: null, organizations: [] });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setState({ loading: false, user: data.user, organization: data.organization, role: data.role,
                 modules: data.modules || [], subscription: null });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setState({ loading: false, user: data.user, organization: data.organization, role: data.role,
                 modules: data.modules || [], subscription: null });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setState({ loading: false, user: null, organization: null, role: null, modules: [], subscription: null, organizations: [] });
  };

  const setOrganization = (organization) => setState((s) => ({ ...s, organization }));

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, reload: load, setOrganization }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useCanEdit() {
  const { role } = useAuth();
  return role === "OWNER" || role === "STAFF";
}

/** True when the org's plan (plus any platform override) includes this module. */
export function useHasModule(key) {
  const { modules } = useAuth();
  return (modules || []).includes(key);
}
